import type { ClauseConditionExpr } from "@/components/ClauseConditionBuilder";
import type { ContractData } from "@/lib/mergeFields";

export type ClauseEvalContext = {
  annualSalary: number;
  contractType: string;
  termYears: number;
  sport: string;
  institutionDivision: string;
};

function termYearsFromDates(startDate?: string, endDate?: string): number {
  if (!startDate || !endDate) return 0;
  try {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    if (isNaN(start) || isNaN(end) || end <= start) return 0;
    return (end - start) / (365.25 * 24 * 60 * 60 * 1000);
  } catch {
    return 0;
  }
}

export function buildClauseEvalContext(data: ContractData): ClauseEvalContext {
  return {
    annualSalary: Number(data.annualSalary ?? 0),
    contractType: data.contractType ?? "",
    termYears: termYearsFromDates(data.startDate, data.endDate),
    sport: data.coachSport ?? "",
    institutionDivision: data.orgDivision ?? "",
  };
}

export function evaluateClauseCondition(
  condition: ClauseConditionExpr | null | undefined,
  ctx: ClauseEvalContext,
): boolean {
  if (!condition) return true;
  switch (condition.kind) {
    case "always":
      return true;
    case "never":
      return false;
    case "field_op": {
      const { field, op, value } = condition;
      let fieldValue: unknown;
      switch (field) {
        case "compensation.base_salary":
          fieldValue = ctx.annualSalary;
          break;
        case "coach.sport":
          fieldValue = ctx.sport.toLowerCase();
          break;
        case "coach.role":
        case "contract.type":
          fieldValue = ctx.contractType;
          break;
        case "term.years":
          fieldValue = ctx.termYears;
          break;
        case "institution.type":
          fieldValue = ctx.institutionDivision;
          break;
        default:
          return false;
      }
      switch (op) {
        case "gt":
          return typeof fieldValue === "number" && fieldValue > Number(value);
        case "gte":
          return typeof fieldValue === "number" && fieldValue >= Number(value);
        case "lt":
          return typeof fieldValue === "number" && fieldValue < Number(value);
        case "lte":
          return typeof fieldValue === "number" && fieldValue <= Number(value);
        case "eq":
          return String(fieldValue) === String(value);
        case "neq":
          return String(fieldValue) !== String(value);
        case "in":
          return (
            Array.isArray(value) &&
            (value as string[]).includes(String(fieldValue))
          );
        case "contains":
          return (
            typeof fieldValue === "string" &&
            fieldValue.toLowerCase().includes(String(value).toLowerCase())
          );
        default:
          return false;
      }
    }
    case "and":
      return condition.exprs.every((e) => evaluateClauseCondition(e, ctx));
    case "or":
      return condition.exprs.some((e) => evaluateClauseCondition(e, ctx));
    default:
      return false;
  }
}

export type ClauseInclusionStatus = "required" | "suggested" | "neutral";

export function getClauseInclusionStatus(
  clause: { inclusionCondition?: ClauseConditionExpr | null; inclusionRequired?: boolean | null },
  ctx: ClauseEvalContext,
): ClauseInclusionStatus {
  const condition = clause.inclusionCondition ?? { kind: "always" as const };
  const matches = evaluateClauseCondition(condition, ctx);
  if (!matches) return "neutral";
  // An "always" condition without inclusionRequired isn't a meaningful signal.
  if (condition.kind === "always" && !clause.inclusionRequired) return "neutral";
  return clause.inclusionRequired ? "required" : "suggested";
}
